import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamRosterMember, TeamSummary } from '@gg/shared';
import { ApiRequestError } from '@/lib/api';
import { useReplaceTeamMember, useResendTeamMember } from '@/lib/queries';

/**
 * Roster rows of a team (screen 06 "Équipe « … »", P-20 step 6): each seat with its state,
 * _Dossier OK_ in `success` once the member's documents are complete, _Rejoint·e_ / _Invité·e_
 * otherwise. With `actions` (the captain, on /me/teams/:id) an invited seat can be resent or
 * replaced; joined members cannot be removed in self-service.
 */
export function Roster({ team, actions = false }: { team: TeamSummary; actions?: boolean }) {
  const { t } = useTranslation();
  return (
    <ul className="m-0 flex list-none flex-col p-0 text-sm">
      {team.members.map((m) => (
        <li
          key={m.id}
          className="flex items-center justify-between gap-3 border-t border-line py-2"
        >
          <span className="truncate" title={m.email}>
            {m.firstName ? `${m.firstName} ${m.lastName ?? ''}`.trim() : m.email}
            {m.isCaptain && <span className="text-ink-muted"> {t('teams.captainSuffix')}</span>}
          </span>
          <SeatState member={m} teamId={team.id} actions={actions && team.isCaptain} />
        </li>
      ))}
    </ul>
  );
}

function SeatState({
  member: m,
  teamId,
  actions,
}: {
  member: TeamRosterMember;
  teamId: string;
  actions: boolean;
}) {
  const { t } = useTranslation();
  const resend = useResendTeamMember(teamId);
  const replace = useReplaceTeamMember(teamId);
  const [note, setNote] = useState<string | null>(null);

  if (m.status === 'joined') {
    return m.documentsComplete ? (
      <span className="shrink-0 font-semibold text-success">{t('teams.status.docsOk')}</span>
    ) : (
      <span className="shrink-0 font-semibold text-success">{t('teams.status.joined')}</span>
    );
  }
  return (
    <span className="flex shrink-0 flex-col items-end gap-0.5">
      <span className="flex items-center gap-2.5">
        <span className="text-ink-muted">{t('teams.status.invited')}</span>
        {actions && (
          <>
            <button
              type="button"
              disabled={resend.isPending}
              onClick={() => {
                setNote(null);
                resend.mutate(m.id, {
                  onSuccess: () => setNote(t('teams.resent')),
                  onError: (err) =>
                    setNote(
                      err instanceof ApiRequestError && err.code === 'rate_limited'
                        ? t('teams.error.rate_limited')
                        : t('error.generic'),
                    ),
                });
              }}
              className="font-semibold text-accent hover:text-accent-hover"
            >
              {t('teams.resend')}
            </button>
            <button
              type="button"
              disabled={replace.isPending}
              onClick={() => {
                if (window.confirm(t('teams.replaceConfirm', { email: m.email }))) {
                  replace.mutate(m.id, { onError: () => setNote(t('error.generic')) });
                }
              }}
              className="font-semibold text-accent hover:text-accent-hover"
            >
              {t('teams.replace')}
            </button>
          </>
        )}
      </span>
      {note && (
        <span role="status" className="text-helper text-ink-muted">
          {note}
        </span>
      )}
    </span>
  );
}
